"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSession } from "next-auth/react";
import { ACTIVE_ORGANIZATION_STORAGE_KEY } from "@/constants/storage-keys";
import {
  clearPersistedActiveOrganizationId,
  persistActiveOrganizationId,
} from "@/lib/active-organization";
import type { AuthzOrganization } from "@/types/authz";

export type ActiveOrganizationContextValue = {
  organizations: AuthzOrganization[];
  activeOrganization: AuthzOrganization | null;
  activeOrganizationId: string | null;
  setActiveOrganizationId: (id: string) => void;
};

const ActiveOrganizationContext = createContext<ActiveOrganizationContextValue | null>(null);

export function ActiveOrganizationProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession();
  const organizations = session?.user?.organizations ?? [];

  const organizationIdsKey = useMemo(
    () =>
      organizations
        .map((t) => t.id)
        .slice()
        .sort()
        .join("|"),
    [organizations],
  );

  const [activeOrganizationId, setActiveOrganizationIdState] = useState<string | null>(null);

  useEffect(() => {
    if (status === "loading") return;

    const frame = requestAnimationFrame(() => {
      if (organizations.length === 0) {
        setActiveOrganizationIdState(null);
        clearPersistedActiveOrganizationId();
        return;
      }

      if (organizations.length === 1) {
        const id = organizations[0].id;
        setActiveOrganizationIdState(id);
        persistActiveOrganizationId(id);
        return;
      }

      const ids = new Set(organizations.map((t) => t.id));

      setActiveOrganizationIdState((current) => {
        if (current && ids.has(current)) {
          persistActiveOrganizationId(current);
          return current;
        }

        try {
          const stored = localStorage.getItem(ACTIVE_ORGANIZATION_STORAGE_KEY);
          if (stored && ids.has(stored)) {
            persistActiveOrganizationId(stored);
            return stored;
          }
        } catch {
          /* ignore */
        }

        const fallbackId = organizations[0]?.id ?? null;
        if (fallbackId) {
          persistActiveOrganizationId(fallbackId);
        }
        return fallbackId;
      });
    });

    return () => cancelAnimationFrame(frame);
  }, [status, organizationIdsKey, organizations]);

  const setActiveOrganizationId = useCallback(
    (id: string) => {
      const ids = new Set(organizations.map((t) => t.id));
      if (!ids.has(id)) return;
      setActiveOrganizationIdState(id);
      persistActiveOrganizationId(id);
    },
    [organizations],
  );

  const activeOrganization = organizations.find((t) => t.id === activeOrganizationId) ?? null;

  const value = useMemo(
    (): ActiveOrganizationContextValue => ({
      organizations,
      activeOrganization,
      activeOrganizationId,
      setActiveOrganizationId,
    }),
    [organizations, activeOrganization, activeOrganizationId, setActiveOrganizationId],
  );

  return (
    <ActiveOrganizationContext.Provider value={value}>
      {children}
    </ActiveOrganizationContext.Provider>
  );
}

export function useActiveOrganization(): ActiveOrganizationContextValue {
  const ctx = useContext(ActiveOrganizationContext);
  if (!ctx) {
    throw new Error("useActiveOrganization must be used within ActiveOrganizationProvider");
  }
  return ctx;
}
